/**
 * QUIPWA — DashboardPage v2
 *
 * Columna central: bloques de estudio con hora del evento de Calendar.
 * Columna derecha: solo mazos con eventos de Calendar VENCIDOS (antes de ahora).
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDecks } from '../hooks/useDecks'
import { DeckHealthCard } from '../components/deck/DeckHealthCard'
import { useDashboardStats } from '../hooks/useDashboardStats'
import { StudyBlock } from '../components/dashboard/StudyBlock'
import { EmptyState } from '../components/dashboard/EmptyState'
import { useNotes } from '../hooks/useNotes'
import { FlashcardCreationModal } from '../components/notes/FlashcardCreationModal'
import { ArrowRight, FileText, Zap } from 'lucide-react'
import { useReviewNowPlan } from '../hooks/useReviewNowPlan'
import type { ReviewNowPlan } from '../lib/fsrs/review-now'
import { useI18n, type TFunction } from '../lib/i18n/i18n-store'
import { getStateModified } from '../lib/auth/local-storage'
import { useLoadingIndicator } from '../hooks/useLoadingIndicator'
import { LoadingState } from '../components/system/LoadingState'
import { lcdCounter, lcdGhost } from '../lib/ui/lcd'
import '../styles/Dashboard.css'

// ─── Format Helpers ──────────────────────────────────────────────────────────

/** How long ago the last Drive sync landed. Unix seconds in, phrase out. */
function formatSyncAge(ts: number, t: TFunction): string {
  const diffMs = Date.now() - ts * 1000
  if (diffMs < 60_000) return t('time.justNow')
  if (diffMs < 3_600_000) return t('time.minutesAgo', { n: Math.floor(diffMs / 60_000) })
  if (diffMs < 86_400_000) return t('time.hoursAgo', { n: Math.floor(diffMs / 3_600_000) })
  return t('time.daysAgo', { n: Math.floor(diffMs / 86_400_000) })
}

function formatBigDate() {
  const d = new Date()
  const dayName   = new Intl.DateTimeFormat('es-ES', { weekday: 'long' }).format(d)
  const dayNum    = d.getDate()
  const monthName = new Intl.DateTimeFormat('es-ES', { month: 'long' }).format(d)
  const year      = d.getFullYear()
  return { dayName, dayNum, monthName, year }
}

// ─── «Repasar ahora» ─────────────────────────────────────────────────────────

/**
 * The page's one primary action: everything due in every deck, as one
 * session. The numbers come from the session's own queue (useReviewNowPlan),
 * so they are exactly what will be studied.
 */
function ReviewNowButton({ plan, onStart }: { plan: ReviewNowPlan; onStart: () => void }) {
  const { t } = useI18n()
  const parts = [
    plan.cards === 1 ? t('dashboard.reviewNowCardsOne') : t('dashboard.reviewNowCards', { count: plan.cards }),
    plan.decks === 1 ? t('dashboard.reviewNowDecksOne') : t('dashboard.reviewNowDecks', { count: plan.decks }),
    t('dashboard.reviewNowMinutes', { count: plan.minutes }),
  ]
  // The same plan on the «Fichero» skin's display, as counters («015 FICHAS ·
  // 01 CAJÓN · ~05 MIN»). Hidden in the flat skin (Dashboard.css) and from
  // readers, who get the sentence above it, which the skin keeps for them.
  const lcd = [
    `${lcdCounter(plan.cards)} ${t(plan.cards === 1 ? 'dashboard.lcdCard' : 'dashboard.lcdCards')}`,
    `${lcdCounter(plan.decks, 2)} ${t(plan.decks === 1 ? 'dashboard.lcdDeck' : 'dashboard.lcdDecks')}`,
    `~${lcdCounter(plan.minutes, 2)} min`,
  ].join(' · ')
  return (
    <button type="button" className="btn btn--primary dash-review-now" onClick={onStart} title={t('dashboard.reviewNowHint')}>
      <span className="led led--on" aria-hidden="true" />
      <span className="dash-review-now-text">
        <span className="dash-review-now-label">{t('dashboard.reviewNow')}</span>
        <span className="dash-review-now-meta">{parts.join(' · ')}</span>
        <span className="lcd dash-review-now-lcd" data-ghost={lcdGhost(lcd)} aria-hidden="true">{lcd}</span>
      </span>
      <ArrowRight size={20} aria-hidden="true" className="dash-review-now-icon" />
    </button>
  )
}

// ─── DashboardPage ────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const { data: decks, isLoading: decksLoading } = useDecks()
  const showDecksLoading = useLoadingIndicator(decksLoading)
  const {
    calLoading,
    activeDecks,
    pendingDecks,
    deckTaskMap,
    totalPending
  } = useDashboardStats(decks)

  // Notes surfaced here so the Dashboard — the screen most people see every
  // day — reflects that notes and card review are one flow, not two separate
  // features (a note is potential review material, not a bolt-on).
  const { data: reviewPlan } = useReviewNowPlan()

  const { notes } = useNotes()
  const recentNotes = [...notes].sort((a, b) => b.modified_at - a.modified_at).slice(0, 3)
  const [cardModalNote, setCardModalNote] = useState<{ id: string; title: string } | null>(null)
  const [lastSyncedAt] = useState(getStateModified)

  // "Vencidos" is a subset of the decks with work to do, so rendering both
  // lists unfiltered showed the same deck twice in two different chromes.
  // Each deck now appears exactly once: escalated ones on the right, the rest
  // in the centre.
  const overdueIds = new Set(pendingDecks.map(d => d.instance.id))
  const centreDecks = activeDecks.filter(d => !overdueIds.has(d.instance.id))
  const idleDecks = decks?.filter(
    d => !activeDecks.includes(d) && !overdueIds.has(d.instance.id)
  ) ?? []

  if (showDecksLoading) return <LoadingState label={t('common.loading')} />

  if (!decks || decks.length === 0) {
    return <EmptyState onImport={() => navigate('/import')} />
  }

  const { dayName, dayNum, monthName, year } = formatBigDate()

  return (
    <div className="dashboard-layout">

      {/* Zona 1: Columna Central (Fecha + Bloques de Estudio) */}
      <div className="dash-col-center">
        <header className="dash-big-date">
          <div className="date-weekday">{dayName}</div>
          {/* The date is what reads as this page's title, so it's the h1. The
              page had none: four h2 sections hanging off nothing. */}
          <h1 className="date-main">
            <span className="date-num">{dayNum}</span>
            <span className="date-month-year">de {monthName}, {year}</span>
          </h1>
          {calLoading && <div className="dash-cal-loading">{t('dashboard.calSyncing')}</div>}
        </header>

        {reviewPlan && reviewPlan.cards > 0 && (
          <ReviewNowButton plan={reviewPlan} onStart={() => navigate('/review')} />
        )}

        <section className="dash-section">
          <h2 className="dash-section-title">{t('dashboard.activeDecks')}</h2>
          <div className="dash-block-list">
            {centreDecks.length > 0 ? (
              centreDecks.map(deck => (
                <StudyBlock
                  key={deck.instance.id}
                  deck={deck}
                  task={deckTaskMap.get(deck.instance.id)}
                  onClick={() => navigate(`/deck/${deck.instance.id}`)}
                  onStudy={() => navigate(`/deck/${deck.instance.id}/session`)}
                />
              ))
            ) : (
              <p className="dash-empty-msg">{t('dashboard.allCaughtUp')}</p>
            )}
          </div>
        </section>

        {/* Notas recientes — mismo peso visual que "Mazos activos": ninguno domina */}
        {recentNotes.length > 0 && (
          <section className="dash-section">
            <h2 className="dash-section-title">{t('dashboard.recentNotes')}</h2>
            <div className="dash-block-list">
              {recentNotes.map(note => (
                <div key={note.file_id} className="note-block">
                  <FileText size={16} className="note-block-icon" />
                  <button
                    className="note-block-open"
                    onClick={() => navigate(`/notes/${note.file_id}`)}
                  >
                    <span className="note-block-name">{note.title}</span>
                    <span className="note-block-meta">
                      {note.domain && <span className="note-block-domain">{note.domain}</span>}
                      {note.tags.slice(0, 2).map(tag => <span key={tag} className="chip note-block-tag">#{tag}</span>)}
                    </span>
                  </button>
                  <button
                    className="btn btn--soft note-block-btn"
                    title={t('dashboard.makeFlashcard')}
                    aria-label={`${t('dashboard.makeFlashcard')}: ${note.title}`}
                    onClick={() => setCardModalNote({ id: note.file_id, title: note.title })}
                  >
                    <Zap size={13} style={{ fill: 'currentColor' }} />
                    {t('dashboard.flashcard')}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Decks sin actividad reciente */}
        {idleDecks.length > 0 && (
          <section className="dash-section">
            {/* Was opacity: 0.5, i.e. 2.2:1 — and this is an empty state, so
                it's the only thing on screen to read. Dimmed by colour now. */}
            <h2 className="dash-section-title dash-section-title--quiet">{t('dashboard.noScheduled')}</h2>
            <div className="dash-block-list">
              {idleDecks.map(deck => (
                <DeckHealthCard
                  key={deck.instance.id}
                  instance={deck.instance}
                  stats={deck.stats}
                  onClick={() => navigate(`/deck/${deck.instance.id}`)}
                  onStudy={() => navigate(`/deck/${deck.instance.id}/session`)}
                />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Zona 2: Columna Derecha (Pendientes vencidos por Calendar) */}
      <div className="dash-col-right">
        <section className="dash-section">
          <div className="dash-section-header">
            <h2 className="dash-section-title">{t('dashboard.overdue')} · {totalPending}</h2>
          </div>
          {/* The count above is only as fresh as the last Drive sync. Saying so
              is the honest replacement for the pulsing dot that used to sit
              here: QUIPWA has no server, so nothing on this screen is live. */}
          <p className="dash-sync-stamp">
            {lastSyncedAt > 0
              ? t('dashboard.syncedAgo', { when: formatSyncAge(lastSyncedAt, t) })
              : t('dashboard.neverSynced')}
          </p>

          <div className="dash-block-list">
            {pendingDecks.length > 0 ? (
              pendingDecks.map(deck => (
                <DeckHealthCard
                  key={deck.instance.id}
                  instance={deck.instance}
                  stats={deck.stats}
                  onClick={() => navigate(`/deck/${deck.instance.id}`)}
                  onStudy={() => navigate(`/deck/${deck.instance.id}/session`)}
                />
              ))
            ) : (
              // Only overdue cards count here: "all caught up" contradicted
              // «Repasar ahora» whenever there were cards due today or new ones.
              <p className="dash-empty-msg">{t('dashboard.nothingOverdue')}</p>
            )}
          </div>
        </section>
      </div>

      <FlashcardCreationModal
        isOpen={cardModalNote !== null}
        onClose={() => setCardModalNote(null)}
        defaultBack=""
        sourceNoteId={cardModalNote?.id ?? ''}
        sourceNoteTitle={cardModalNote?.title ?? ''}
      />

    </div>
  )
}